import * as React from 'react';
import cx from 'classnames';
import {Flex} from '@common/components/Flex';
import {I18n} from 'react-redux-i18n';
import {getTestAttributes} from '@common/utils/tests';
import {Loading} from '@common/components/loading';
import {EmailTemplateListDto} from '@common/dto/email-template/list';
import {Ellipsis} from '@common/components/Ellipsis';
import {formatDate} from '@common/utils/date';

import './templates.styles.scss';

export type TemplatesProps = {
    moduleName: string;
    selectedTemplates: Set<number>;
    onSelectedTemplateChange: (templates: Array<number>) => void;
    className?: string;
    loading?: boolean;
    data: Array<EmailTemplateListDto>;
};

export const Templates: React.FC<TemplatesProps> = (props) => {
    const {selectedTemplates, onSelectedTemplateChange, className, moduleName, loading, data} = props;
    const localModuleName = `${moduleName}-questionnaires-list`;
    const itemRefs = React.useRef<Record<number, HTMLDivElement | null>>({})

    const checkIsSelected = React.useCallback((id: number) =>
            selectedTemplates.has(id),
        [selectedTemplates]);

    const handleSelectionChange = React.useCallback((e: React.MouseEvent<HTMLDivElement>, template: EmailTemplateListDto) => {
        if (e.ctrlKey || e.shiftKey) {
            const isSelected = checkIsSelected(template.id);
            let updatedSelectedItems: Array<number>;

            if (isSelected) {
                updatedSelectedItems = [...selectedTemplates].filter((selectedItem) => selectedItem !== template.id);
            } else {
                updatedSelectedItems = [...selectedTemplates, template.id];
            }

            onSelectedTemplateChange(updatedSelectedItems);
        } else {
            onSelectedTemplateChange([template.id]);
        }
    }, [checkIsSelected, selectedTemplates]);

    React.useEffect(() => {
        if (selectedTemplates.size == 1) {
            const el = itemRefs.current[[...selectedTemplates][0] ?? -1];
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedTemplates, data])

    const renderDate = (item: EmailTemplateListDto) => {
        if (item.modifieddate) {
            const date = new Date(item.modifieddate);

            return formatDate(date, {format: 'YYYY-MM-DD'})
        }
        return '';
    }

    const renderName = (item: EmailTemplateListDto) => {
        const imgWidth = 16;
        const imgMargin = 4;

        return (
            <Flex alignItems="center" width="100%">
                <img src="png/email.png" style={{marginRight: imgMargin, marginLeft: imgMargin}} width={imgWidth}/>
                <Ellipsis
                    width={`calc(100% - ${imgWidth + imgMargin * 2}px)`}
                    text={item.name}
                />
            </Flex>
        );
    };

    const columns = React.useMemo(() => {
        type Column = {
            name: string;
            fieldName: keyof EmailTemplateListDto;
            width?: string;
            render: (item: EmailTemplateListDto) => React.ReactNode;
        };

        const cols: Array<Column> = [
            {
                name: "Template Name",
                fieldName: "name",
                width: '35%',
                render: renderName,
            },
            {name: "Subject", fieldName: "subject", width: '35%'},
            {
                name: "Modified",
                fieldName: "modifieddate",
                width: '20%',
                render: renderDate,
            },
        ].map((x) => ({...x, name: I18n.t(x.name)} as Column));

        return cols;
    }, [renderDate, renderName]);

    return (
        <div className={cx("EmailTemplatesList", className)}>
            {loading && (
                <div className="EmailTemplatesList__loading">
                    <Loading size={20}/>
                </div>
            )}
            <Flex
                alignItems="center"
                className="EmailTemplatesList__header"
            >
                {columns.map((x) => (
                    <span key={x.fieldName} style={{width: x.width}} className="EmailTemplatesList__header__cell">
            {x.name}
          </span>
                ))}
            </Flex>
            <div className="EmailTemplatesList__body">
                {data.map((item, i) => {
                    const isSelected = checkIsSelected(item.id)

                    return (
                        <Flex
                            alignItems="center"
                            key={item.id}
                            reference={(el: HTMLDivElement | null) => (itemRefs.current[item.id] = el)}
                            data-id={item.id}
                            className={cx("EmailTemplatesList__body__row", {"EmailTemplatesList__body__row-selected": isSelected})}
                            onClick={(e: React.MouseEvent<HTMLDivElement>) => handleSelectionChange(e, item)}
                            {...getTestAttributes(localModuleName, "item", "row")}
                        >
                            {columns.map((col, i) => {
                                if (col.render) {
                                    return (
                                        <div style={{width: col.width}} className="EmailTemplatesList__body__row__cell"
                                             key={col.fieldName}>
                                            {col.render(item)}
                                        </div>
                                    );
                                }

                                return (
                                    <div key={col.fieldName} className="EmailTemplatesList__body__row__cell"
                                         style={{width: col.width}}>
                                        <Ellipsis
                                            width="100%"
                                            text={item[col.fieldName]}
                                        />
                                    </div>
                                );
                            })}
                        </Flex>
                    );
                })}
            </div>
        </div>
    );
};